import { createContext, useContext, useEffect, useMemo } from "react";
import {
  GaripalliIDB,
  GaripalliIDBDatabaseSchema,
  getGaripalliIDB,
} from "../extensions/idb";

export interface IndexedDbProviderProps<S> {
  children: React.ReactNode;
}

export type SyncMetadata = {
  id: string;
  matches: {
    lastSyncedAt: number;
    lastItemId?: string;
  };
  match_events: {
    lastSyncedAt: number;
    lastItemId?: string;
  };
};

const IndexedDbContext = createContext<
  { getDb: () => Promise<GaripalliIDB> } | undefined
>(undefined);

export const AppIndexedDbProvider: React.FC<
  IndexedDbProviderProps<GaripalliIDBDatabaseSchema>
> = ({ children }) => {
  useEffect(() => {
    getGaripalliIDB()
      .then(async (db) => {
        const syncMetadataStore = db.objectStore("sync_metadata");
        if (!(await syncMetadataStore.get("lastSync"))) {
          await syncMetadataStore
            .add({
              id: "lastSync",
              matches: { lastSyncedAt: 0, challengeStatsCalculated: false },
              match_events: { lastSyncedAt: 0 },
            })
            .finally(() => db.close());
        }
      })
      .catch((error) => {
        console.error("Error initializing IndexedDB:", error);
      });
  }, []);

  const value = useMemo(
    () => ({
      getDb: async () => {
        return await getGaripalliIDB();
      },
    }),
    []
  );

  return (
    <IndexedDbContext.Provider value={value}>
      {children}
    </IndexedDbContext.Provider>
  );
};

// Custom hook to use IndexedDb
export const useIndexedDb = (): { getDb: () => Promise<GaripalliIDB> } => {
  const context = useContext(IndexedDbContext);

  if (context == null) {
    throw new Error("useIndexedDb must be used within an IndexedDbProvider");
  }

  return context;
};
